import { Hotspot, HotspotContainer } from 'marzipano';
import { inject } from '@angular/core';
import { LoggingService } from '@newroom-connect/library/services';
import { ISphericalCoords } from '@newroom-connect/library/interfaces';

import { IMarzipanoHotspotConfig, IMarzipanoPerspective } from '../interfaces/marzipano-scene.interface';

export interface ISceneHostpotContainer {
  id: string;
  hotspot: Hotspot;
  dragOverlayHotspot: Hotspot;
  transformOverlayHotspot: Hotspot;
}

/**
 * Manages hotspots for a Marzipano scene.
 * All hotspot-related functionality is handled independently from the main scene management.
 * Handles creation, removal, positioning, and transform updates for hotspots.
 *
 */
export class MarzipanoSceneHotspot {
  private readonly logger = inject(LoggingService);

  public _hotspotContainer: HotspotContainer | null = null;

  public _hotspots = new Map<string, ISceneHostpotContainer>();
  public _hotspotConfigs = new Map<string, IMarzipanoHotspotConfig>();

  /**
   * Initialize the hotspot manager with a Marzipano hotspot container.
   *
   * @param hotspotContainer - The Marzipano hotspot container from a scene.
   */
  public initialize(hotspotContainer: HotspotContainer): void {
    this._hotspotContainer = hotspotContainer;
  }

  /**
   * Add a hotspot to the scene.
   *
   * @param config - Hotspot configuration.
   * @param dragOverlayElement - The element for the drag overlay.
   * @param transformOverlayElement - The element for the transform overlay.
   *
   * @returns The created hotspot instance.
   * @throws Error if the hotspot container is not available.
   */
  public addHotspot(config: IMarzipanoHotspotConfig, dragOverlayElement: HTMLElement, transformOverlayElement: HTMLElement): ISceneHostpotContainer {
    if (!this._hotspotContainer || !config.element) {
      throw new Error('Hotspot container not available or element missing. Initialize first and provide element.');
    }

    try {
      const hotspotOptions = config.perspective ? {
        perspective: {
          radius: config.perspective.radius,
          extraTransforms: config.perspective.extraTransforms || 'translate(-50%, -50%)'
        }
      } : undefined;

      const hotspot = this._hotspotContainer.createHotspot(config.element, config.position, hotspotOptions);

      const dragOverlayHotspot = this._hotspotContainer.createHotspot(dragOverlayElement, config.position, hotspotOptions);
      const transformOverlayHotspot = this._hotspotContainer.createHotspot(transformOverlayElement, config.position, {
        perspective: {
          ...hotspotOptions?.perspective,
          extraTransforms: 'translate(-50%, -50%)'
        }
      });

      const hotspotContainer: ISceneHostpotContainer = {
        id: config.id,
        hotspot,
        dragOverlayHotspot,
        transformOverlayHotspot
      };

      // Store hotspot and config for tracking
      this._hotspots.set(config.id, hotspotContainer);
      this._hotspotConfigs.set(config.id, config);

      // Handle visibility
      if (config.visible === false && config.element) {
        config.element.style.display = 'none';
      }

      return hotspotContainer;
    } catch (error) {
      this.logger.error('Failed to add hotspot', error as Error);
      throw error;
    }
  }

  /**
   * Remove a hotspot from the scene by ID.
   *
   * @param hotspotId - The ID of the hotspot to remove.
   */
  public removeHotspotById(hotspotId: string): void {
    const hotspotLayer = this._hotspots.get(hotspotId);

    if (!hotspotLayer || !this._hotspotContainer) {
      this.logger.warn('Cannot remove hotspot: hotspot or container not found', { hotspotId });
      return;
    }

    try {
      this._hotspotContainer.destroyHotspot(hotspotLayer.hotspot);
      this._hotspotContainer.destroyHotspot(hotspotLayer.dragOverlayHotspot);
      this._hotspotContainer.destroyHotspot(hotspotLayer.transformOverlayHotspot);

      // Remove from tracking maps
      this._hotspots.delete(hotspotId);
      this._hotspotConfigs.delete(hotspotId);
      this.logger.debug('Hotspot removed', { hotspotId });
    } catch (error) {
      this.logger.error('Failed to remove hotspot', error as Error);
    }
  }

  /**
   * Update hotspot transform with CSS transform string for perspective distortion.
   * Delegates to MarzipanoHotspotTransform manager.
   *
   * @param hotspotId - The hotspot ID to update.
   * @param perspective - Perspective configuration.
   * @param position - Position configuration.
   */
  public updateHotspotTransform(hotspotId: string, perspective: IMarzipanoPerspective, position: ISphericalCoords): void {
    const hotspotLayer = this._hotspots.get(hotspotId);
    const config = this._hotspotConfigs.get(hotspotId);

    if (!hotspotLayer || !config) {
      this.logger.warn('Cannot update hotspot transform: hotspot not found', { hotspotId });
      return;
    }

    const newPerspective = {
      radius: perspective.radius,
      extraTransforms: perspective.extraTransforms || 'translate(-50%, -50%)'
    };

    // Delegate to transform manager
    hotspotLayer.hotspot.setPerspective(newPerspective);
    hotspotLayer.hotspot.setPosition(position);

    hotspotLayer.dragOverlayHotspot.setPerspective(newPerspective);
    hotspotLayer.dragOverlayHotspot.setPosition(position);

    // Do not update transform overlay perspective, only position.
    hotspotLayer.transformOverlayHotspot.setPosition(position);
  }

  /**
   * Dispose of the hotspot manager and clean up resources.
   */
  public dispose(): void {
    // Clear all hotspots
    if (this._hotspotContainer) {
      try {
        for (const hotspot of this._hotspots.values()) {
          this._hotspotContainer.destroyHotspot(hotspot.hotspot);
          this._hotspotContainer.destroyHotspot(hotspot.dragOverlayHotspot);
          this._hotspotContainer.destroyHotspot(hotspot.transformOverlayHotspot);
        }
      } catch (error) {
        this.logger.error('Failed to clear hotspots during disposal', error as Error);
      }
    }

    // Clear tracking maps
    this._hotspots.clear();
    this._hotspotConfigs.clear();
    this._hotspotContainer = null;

    this.logger.debug('MarzipanoSceneHotspot disposed');
  }
}
